import { useState } from 'react';

import PropTypes from 'prop-types';
import { generatePath, useNavigate, useLocation } from 'react-router-dom';

import MoreHorizRoundedIcon from '@mui/icons-material/MoreHorizRounded';
import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import { useExperimentFlag } from '@zenbusiness/application-commons-experiments';
import { useIntl } from '@zenbusiness/application-commons-intl';
import Chip from '@zenbusiness/zui/Chip';
import IconButton from '@zenbusiness/zui/IconButton';
import Menu from '@zenbusiness/zui/Menu';
import MenuItem from '@zenbusiness/zui/MenuItem';
import Stack from '@zenbusiness/zui/Stack';
import Text from '@zenbusiness/zui/Text';

import * as ROUTES from '#client/constants/routes';

import {
  ENABLE_DASHBOARD_SUBSCRIPTION_DNR,
  ENABLE_DASHBOARD_SUBSCRIPTION_RESTART,
  ENABLE_M2A,
} from '../../../constants/experimentsFlags';
import { faqs, SQSP_DOMAIN_TIER_SERVICE_PREFIX } from '../constants';
import { useChangeRequestContext, useSetChangeRequest } from '../hooks/useChangeRequestContext';
import getServiceVersionless from '../utils/getServiceVersionless';
import { isCustomerActionAllowed, shouldRouteTrialResume } from '../utils/selfServiceDnrEligibility';

function ThreeDotMenu({
  subscription,
  paymentStatus,
  canChangePeriod,
  billingChangeSavings,
  domesticJurisdiction,
  isIncluded = false,
  businessEntityUuid,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const { changeFlow } = useChangeRequestContext();
  const { setChangeFlow } = useSetChangeRequest();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { formatMessage } = useIntl();

  const subscriptionRestartEnabled = useExperimentFlag(ENABLE_DASHBOARD_SUBSCRIPTION_RESTART, false);
  const m2aEnabled = useExperimentFlag(ENABLE_M2A, false);
  const subscriptionDnrEnabled = useExperimentFlag(ENABLE_DASHBOARD_SUBSCRIPTION_DNR, false);

  const { subscriptionStatus, isFormationComplete, isPostPurchaseComplete, service, orderBillingItemUuid } =
    subscription;

  const serviceVersionless = getServiceVersionless(service);

  const { modalFlow } = changeFlow;
  const { restartSubscription, changeBillingIntervalAnnual } = modalFlow;

  const canUseLegacyDnrSupportFallback =
    !['cancel_renewal'].includes(subscriptionStatus) &&
    paymentStatus &&
    paymentStatus !== 'failed' &&
    isFormationComplete;
  // Legacy restart/change-billing modals can still open an inline support
  // fallback. Keep that behavior separate from the menu's Contact Support row.
  const requiresSupportModalFlow =
    paymentStatus === 'failed' || ['deferred', 'created', 'unpaid'].includes(subscriptionStatus);
  const shouldNotSeeMenu = ['cancel_renewal_pending'].includes(subscriptionStatus);
  const canChangeBillingInterval =
    m2aEnabled &&
    canChangePeriod &&
    paymentStatus !== 'failed' &&
    isPostPurchaseComplete &&
    !['cancel_renewal_pending', 'cancel_renewal', 'active_pending'].includes(subscriptionStatus);

  const hasFAQ = faqs[serviceVersionless];

  // Jurisdiction limits gate the legacy Contact Support fallback for pending
  // statuses (deferred/created/unpaid). null = all jurisdictions allowed.
  const jurisdictionLimitsDefault = ['CA', 'CO', 'DE', 'NY', 'IL', 'HI', 'TN', 'DC'];

  const serviceConfigs = {
    registered_agent_service: { jurisdictionLimits: null },
    zb_money_pro: { jurisdictionLimits: jurisdictionLimitsDefault },
    pro_plan: { jurisdictionLimits: null },
    worry_free_service: { jurisdictionLimits: null },
    premium_plan: { jurisdictionLimits: null },
  };

  const isDnrAllowedForJurisdiction = (jurisdiction) => {
    const config = serviceConfigs[serviceVersionless];
    if (config?.jurisdictionLimits === null) {
      return true;
    }
    const limits = config?.jurisdictionLimits ?? jurisdictionLimitsDefault;
    return limits.includes(jurisdiction);
  };

  const showDoNotRenewLabel = service?.startsWith(SQSP_DOMAIN_TIER_SERVICE_PREFIX);
  const showCancelTrialItem = isCustomerActionAllowed(subscription, 'cancelTrial');
  const showCancelSubscriptionItem = subscriptionDnrEnabled && isCustomerActionAllowed(subscription, 'doNotRenew');
  const showRestartSubscriptionItem =
    subscriptionRestartEnabled && isCustomerActionAllowed(subscription, 'undoDoNotRenew');
  const showResumeTrialItem = isCustomerActionAllowed(subscription, 'resumeTrial');
  // Intentional legacy path: pending statuses that cannot self-service cancel
  // get Contact Support → help when jurisdiction allows. Mutually exclusive
  // with the default Contact Support row below.
  const showLegacySupportFallback =
    canUseLegacyDnrSupportFallback &&
    isDnrAllowedForJurisdiction(domesticJurisdiction) &&
    requiresSupportModalFlow &&
    !isIncluded &&
    !showCancelTrialItem;

  const handleOpenRestartModalFlow = () => {
    setChangeFlow((prevState) => ({
      ...prevState,
      modalFlow: {
        ...modalFlow,
        ...(requiresSupportModalFlow
          ? {
              error: true,
              restartSubscription: { ...restartSubscription, start: true },
            }
          : {
              restartSubscription: { ...restartSubscription, start: true, stepOne: true },
            }),
      },
    }));
  };

  const handleOpenM2AModalFlow = () =>
    setChangeFlow((prevState) => ({
      ...prevState,
      modalFlow: {
        ...modalFlow,
        ...(requiresSupportModalFlow
          ? {
              error: true,
              changeBillingIntervalAnnual: { ...changeBillingIntervalAnnual, start: true },
            }
          : {
              changeBillingIntervalAnnual: { ...changeBillingIntervalAnnual, start: true, stepOne: true },
            }),
      },
    }));

  const open = Boolean(anchorEl);

  const handleOpenDropdown = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (action) => () => {
    if (action) {
      action();
    }
    setAnchorEl(null);
  };

  const trackingData = {
    elementName: 'ThreeDotMenu',
    elementValue: service,
    appSection: 'business',
    appPageName: 'active_subscription_list',
    canonicalPath: pathname,
  };

  const trackContactCS = useButtonPressedCallback({ ...trackingData, elementText: 'Contact Support' });
  const trackCancelTrial = useButtonPressedCallback({ ...trackingData, elementText: 'Cancel Trial' });
  const trackCancelSubscription = useButtonPressedCallback({ ...trackingData, elementText: 'Cancel Subscription' });
  const trackRestartItem = useButtonPressedCallback({
    ...trackingData,
    elementText: subscription.inTrial ? 'Resume Trial' : 'Restart Subscription',
  });
  const trackChangeBillingInterval = useButtonPressedCallback({ ...trackingData, elementText: 'Change billing' });
  const trackFAQ = useButtonPressedCallback({ ...trackingData, elementText: 'See FAQs' });

  // Dual Contact Support rows are intentional legacy routing:
  // - contact-support: default help entry when status is not pending/failed
  // - contact-support-cancel: jurisdiction-gated fallback for deferred/created/unpaid
  // Show conditions stay mutually exclusive; both navigate to ROUTES.help.
  const config = [
    {
      key: 'contact-support',
      action: () => {
        trackContactCS();
        navigate(ROUTES.help);
      },
      show: !requiresSupportModalFlow && !isIncluded,
      label: formatMessage({ id: 'dashboard.subscriptions.menu.contactSupport' }),
    },
    {
      key: 'contact-support-cancel',
      action: () => {
        trackContactCS();
        navigate(ROUTES.help);
      },
      show: showLegacySupportFallback,
      label: formatMessage({ id: 'dashboard.subscriptions.menu.contactSupport' }),
    },
    {
      key: 'cancel-trial',
      action: () => {
        trackCancelTrial();
        navigate(generatePath(ROUTES.entitySubscriptionCancel, { businessEntityUuid, orderBillingItemUuid }));
      },
      show: showCancelTrialItem,
      label: formatMessage({ id: 'dashboard.subscriptions.menu.cancelTrial' }),
    },
    {
      key: 'cancel-subscription',
      action: () => {
        trackCancelSubscription();
        navigate(generatePath(ROUTES.entitySubscriptionCancel, { businessEntityUuid, orderBillingItemUuid }));
      },
      show: showCancelSubscriptionItem,
      label: formatMessage({
        id: `dashboard.subscriptions.menu.${showDoNotRenewLabel ? 'doNotRenew' : 'cancelSubscription'}`,
      }),
    },
    {
      key: 'restart-subscription',
      action: () => {
        trackRestartItem();
        if (subscription.inTrial && !shouldRouteTrialResume(subscription)) {
          handleOpenRestartModalFlow();
        } else {
          navigate(generatePath(ROUTES.entitySubscriptionRestart, { businessEntityUuid, orderBillingItemUuid }));
        }
      },
      show: subscription.inTrial ? showResumeTrialItem : showRestartSubscriptionItem,
      label: formatMessage({
        id: subscription.inTrial
          ? 'dashboard.subscriptions.menu.resumeTrial'
          : 'dashboard.subscriptions.menu.restartSubscription',
      }),
    },
    {
      key: 'change-billing',
      action: () => {
        trackChangeBillingInterval();
        handleOpenM2AModalFlow();
      },
      show: canChangeBillingInterval,
      label: (
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Text size="sm">{formatMessage({ id: 'dashboard.subscriptions.menu.changeBilling' })}</Text>
          <Chip
            label={formatMessage({ id: 'dashboard.subscriptions.menu.savePct' }, { pct: billingChangeSavings })}
            size="small"
            color="success"
          />
        </Stack>
      ),
    },
    {
      key: 'see-faqs',
      action: () => {
        trackFAQ();
        window.location.href = hasFAQ;
      },
      show: Boolean(hasFAQ),
      label: formatMessage({ id: 'dashboard.subscriptions.menu.seeFaqs' }),
    },
  ];

  const hasMenuItems = config.some(({ show }) => show);

  return (
    <>
      <IconButton
        aria-label="options"
        aria-controls="options-dropdown"
        aria-haspopup="true"
        icon={<MoreHorizRoundedIcon />}
        label={formatMessage({ id: 'dashboard.subscriptions.menu.moreOptions' })}
        clickstreamProps={{ elementName: 'subscription_more_options' }}
        onClick={handleOpenDropdown}
        disabled={!hasMenuItems}
        size="large"
        sx={{ ...(shouldNotSeeMenu && { visibility: 'hidden' }) }}
      />
      {hasMenuItems && (
        <Menu
          id="options-dropdown"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose()}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          disableRestoreFocus
        >
          {config.map(
            ({ key, label, show, action }) =>
              show && (
                <MenuItem key={key} onClick={handleClose(action)}>
                  {typeof label === 'string' ? <Text size="sm">{label}</Text> : label}
                </MenuItem>
              ),
          )}
        </Menu>
      )}
    </>
  );
}

const customerActionPropType = PropTypes.shape({
  allowed: PropTypes.bool,
  reasons: PropTypes.arrayOf(PropTypes.string),
});

ThreeDotMenu.propTypes = {
  subscription: PropTypes.shape({
    subscriptionStatus: PropTypes.string,
    isFormationComplete: PropTypes.bool,
    isPostPurchaseComplete: PropTypes.bool,
    service: PropTypes.string,
    inTrial: PropTypes.bool,
    orderBillingItemUuid: PropTypes.string,
    customerActions: PropTypes.shape({
      doNotRenew: customerActionPropType,
      undoDoNotRenew: customerActionPropType,
      cancelTrial: customerActionPropType,
      resumeTrial: customerActionPropType,
    }),
  }).isRequired,
  paymentStatus: PropTypes.string.isRequired,
  canChangePeriod: PropTypes.bool.isRequired,
  billingChangeSavings: PropTypes.number,
  domesticJurisdiction: PropTypes.string,
  isIncluded: PropTypes.bool,
  businessEntityUuid: PropTypes.string,
};

export default ThreeDotMenu;
